import { faqsPl } from "@data/nordic-landing/faq";
import {
  AlertCircle,
  Battery,
  Calendar,
  CheckCircle,
  Cloud,
  Cpu,
  FileText,
  Gauge,
  Globe,
  LifeBuoy,
  MapPin,
  PenTool,
  RadioTower,
  RefreshCw,
  Search,
  Settings,
  ShieldCheck,
  Smartphone,
  Users,
  Watch,
  Wrench,
} from "lucide-react";

import type { AboutUsCopy } from "@components/bluetooth-landing/AboutUs";
import type { FAQCopy } from "@components/bluetooth-landing/FAQ";
import type { FooterCopy } from "@components/bluetooth-landing/Footer";
import type { HeroCopy } from "@components/bluetooth-landing/Hero";
import type { ProcessCopy } from "@components/bluetooth-landing/Process";
import type { ServicesCopy } from "@components/bluetooth-landing/Services";
import type { TargetAudienceCopy } from "@components/bluetooth-landing/TargetAudience";
import type { ValuePropositionCopy } from "@components/bluetooth-landing/ValueProposition";

export const title =
  "Programowanie Nordic nRF52, nRF53, nRF54 - specjaliści od sprzętu Nordic Semiconductor";
export const description =
  "Jesteśmy zespołem wyspecjalizowanym w sprzęcie Nordic Semiconductor - nRF52, nRF53, nRF54 i Nordic Thingy. Piszemy firmware produkcyjny na nRF Connect SDK i Zephyr RTOS, wraz z towarzyszącą aplikacją mobilną.";

export const structuredData = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Blues Brackets",
    "url": "https://bluesbrackets.com",
    "logo": "https://bluesbrackets.com/favicon-dark.png",
    description,
    "areaServed": "EU",
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "sales",
      "email": "hello@bluesbrackets.com",
      "availableLanguage": ["Polish", "English"],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Programowanie firmware na Nordic nRF52, nRF53 i nRF54",
    "provider": {
      "@type": "Organization",
      "name": "Blues Brackets",
    },
    description,
    "areaServed": "EU",
    "serviceType": "Embedded firmware development",
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqsPl.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a,
      },
    })),
  },
];

export const heroCopy: HeroCopy = {
  header: "Specjaliści od",
  headerTail: "Nordic\xa0nRF52/53/54",
  description:
    "Programujemy firmware na Nordic nRF52, nRF53 i nRF54 oraz prototypujemy na Nordic Thingy. Znamy nRF Connect SDK i Zephyr RTOS od podszewki - od pierwszego bring-upu po produkcję seryjną.",
  cta: {
    text: "Darmowa konsultacja",
    href: "#kontakt",
  },
  secondaryCta: {
    text: "Poznaj nasz proces",
    href: "#proces",
  },
  benefits: [
    { text: "nRF52\xa0nRF53\xa0nRF54", Icon: CheckCircle },
    { text: "nRF Connect SDK i Zephyr RTOS", Icon: CheckCircle },
    { text: "Gotowe na RODO i rynek UE", Icon: CheckCircle },
  ],
};

export const targetAudienceCopy: TargetAudienceCopy = {
  header: {
    h2: "Kim są nasi klienci?",
    h3: "Dla kogo jest ta oferta?",
    p: "Pracujemy z zespołami, które już zdecydowały się na sprzęt Nordic Semiconductor i szukają wykonawcy znającego dokładnie ten stack. Czy któryś z tych punktów pasuje do Twojej sytuacji?",
  },
  points: [
    {
      Icon: Cpu,
      title: "Wybrałeś już chip Nordic i potrzebujesz firmware",
      description:
        "Stawiasz na nRF52832, nRF52840, nRF5340 lub nRF54 i szukasz zespołu, który zna nRF Connect SDK i Zephyr RTOS od pierwszego dnia projektu.",
    },
    {
      Icon: RefreshCw,
      title: "Migrujesz z nRF52 na nRF53 lub nRF54",
      description:
        "Przejście na nowszą serię wiąże się z nowym toolchainem, architekturą dwurdzeniową i innym zestawem peryferiów. Pomagamy zaplanować i przeprowadzić migrację bez niespodzianek.",
    },
    {
      Icon: AlertCircle,
      title: "Twój firmware na Nordic jest niestabilny",
      description:
        "Gubione połączenia BLE, resety, problemy z SDK i bugi. Pomagamy zdiagnozować przyczynę i przywrócić stabilność firmware.",
    },
    {
      Icon: Watch,
      title: "Prototypujesz na Nordic Thingy",
      description:
        "Zaczynasz od gotowej platformy Thingy:52, Thingy:53 lub Thingy:91 i potrzebujesz pomocy, żeby przejść od PoC do dedykowanego PCB z docelowym chipem nRF.",
    },
    {
      Icon: Cloud,
      title: "Potrzebujesz nRF Cloud lub własnego backendu",
      description:
        "Masz doświadczenie z nRF Cloud i wiesz, jak podłączyć urządzenie do tej platformy, ale potrzebujesz też własnego backendu, panelu administracyjnego lub integracji z systemami, których nRF Cloud nie obsługuje.",
    },
  ],
  cta: {
    h4: "Brzmi znajomo?",
    text: "Opowiedz nam, na jakim chipie Nordic pracujesz i co ma robić firmware. Pomożemy oszacować zakres prac.",
    href: "#kontakt",
    buttonText: "Umów darmową konsultację",
  },
};

export const valuePropositionCopy: ValuePropositionCopy = {
  header: {
    h2: "Nasza specjalizacja",
    h3: "Dlaczego warto zlecić nam projekt na sprzęcie Nordic?",
    p: "Nie jesteśmy generycznym zespołem embedded - specjalizujemy się wyłącznie w platformie Nordic Semiconductor. Ta wąska specjalizacja oznacza mniej niespodzianek i szybszy bring-up.",
    imageAlt: "Programowanie firmware na sprzęcie Nordic nRF",
  },
  values: [
    {
      Icon: Cpu,
      title: "Znamy różnice między nRF52, nRF53 i nRF54",
      description:
        "nRF52832, nRF52840, nRF5340, nRF54L15, nRF54H20 - znamy ich zestawy peryferiów, ograniczenia pamięci i pułapki przy uruchamianiu, które u zespołu bez doświadczenia z Nordic potrafią kosztować tygodnie pracy.",
    },
    {
      Icon: Settings,
      title: "Codzienna praca z nRF Connect SDK i Zephyr RTOS",
      description:
        "To obecny kierunek rozwoju Nordic. Znamy też starszy nRF5 SDK i SoftDevice, jeśli rozwijasz istniejący projekt zbudowany na tym fundamencie.",
    },
    {
      Icon: Watch,
      title: "Prototypowanie na Nordic Thingy",
      description:
        "Thingy:52, Thingy:53 i Thingy:91 wykorzystujemy do szybkiej walidacji pomysłu, zanim zainwestujesz w dedykowane PCB z docelowym chipem nRF.",
    },
    {
      Icon: Battery,
      title: "Optymalizacja energetyczna na realnym sprzęcie",
      description:
        "Profilujemy pobór prądu za pomocą Power Profiler Kit, dostrajamy tryby uśpienia i parametry radiowe, żeby urządzenie na baterii działało tak długo, jak wymaga tego produkt.",
    },
    {
      Icon: Globe,
      title: "Europejski zespół, standardy danych UE",
      description:
        "Działamy z Polski i rozumiemy otoczenie regulacyjne dla produktów sprzedawanych w UE - zgodność z RODO oraz wymagania CE i Bluetooth SIG.",
    },
  ],
};

export const servicesCopy: ServicesCopy = {
  header: {
    h2: "Usługi",
    h3: "Programowanie na sprzęcie Nordic Semiconductor",
    p: "Od doboru właściwego chipa po produkcję seryjną. Nasza usługa obejmuje cały cykl życia projektu na platformie Nordic.",
  },
  services: [
    {
      Icon: Search,
      title: "Dobór chipa nRF i architektura",
      description:
        "Analizujemy wymagania funkcjonalne i energetyczne, dobieramy właściwy chip z serii nRF52, nRF53 lub nRF54 i projektujemy architekturę firmware jeszcze przed pierwszą linijką kodu.",
    },
    {
      Icon: Wrench,
      title: "Firmware na nRF Connect SDK i Zephyr RTOS",
      description:
        "Piszemy firmware produkcyjny - sterowniki peryferiów, konfigurację stosu BLE, logikę aplikacji i zarządzanie energią na wybranej serii Nordic.",
    },
    {
      Icon: Watch,
      title: "Prototypowanie na Nordic Thingy",
      description:
        "Budujemy pierwsze PoC na Thingy:52, Thingy:53 lub Thingy:91, a następnie prowadzimy przejście do dedykowanego PCB z docelowym chipem nRF.",
    },
    {
      Icon: RadioTower,
      title: "Integracja Bluetooth LE na stosie Nordic",
      description:
        "Własne profile GATT, konfiguracja rozgłaszania i połączenia, BLE Mesh oraz Auracast (LE Audio) na nRF5340 i nRF54H20.",
    },
    {
      Icon: Gauge,
      title: "Optymalizacja energetyczna",
      description:
        "Profilujemy pobór prądu Power Profiler Kit, dostrajamy tryby uśpienia System ON/OFF i interwały rozgłaszania, żeby wydłużyć czas pracy na baterii.",
    },
    {
      Icon: Smartphone,
      title: "Aplikacja mobilna towarzysząca urządzeniu",
      description:
        "Tworzymy aplikację iOS i Android, która paruje się z urządzeniem na Nordic i obsługuje ten sam profil GATT co firmware.",
    },
    {
      Icon: RefreshCw,
      title: "Aktualizacje OTA i migracje między seriami",
      description:
        "Wdrażamy Nordic Secure Bootloader i protokół nRF DFU oraz pomagamy migrować projekty z nRF52 na nRF53 lub nRF54.",
    },
    {
      Icon: LifeBuoy,
      title: "Utrzymanie i wsparcie firmware",
      description:
        "Po premierze zajmujemy się aktualizacjami zgodności z nowymi wersjami nRF Connect SDK i Zephyr, poprawkami błędów oraz rozwojem funkcji.",
    },
  ],
  cta: {
    h4: "Nie wiesz, który chip Nordic wybrać?",
    p: "Prześlij nam wymagania funkcjonalne i energetyczne Twojego urządzenia. Doradzimy, czy lepiej sprawdzi się nRF52, nRF53 czy nRF54.",
    button: {
      text: "Umów darmową konsultację",
      href: "#kontakt",
    },
  },
};

export const processCopy: ProcessCopy = {
  header: {
    h2: "Nasz Proces",
    h3: "Jak wygląda projekt na sprzęcie Nordic?",
    p: "Przejrzyste etapy i realny sprzęt Nordic w pętli testów od pierwszego dnia. Zobacz, jak przechodzimy od wyboru chipa do firmware gotowego na produkcję.",
  },
  stepNumber: "Krok",
  steps: [
    {
      Icon: Calendar,
      title: "Darmowe spotkanie online",
      duration: "w kilka godzin",
      description:
        "30 minut rozmowy z inżynierem. Opisujesz sprzęt i wymagania, a my dajemy pierwszą ocenę i wskazujemy, który chip Nordic pasuje najlepiej.",
    },
    {
      Icon: FileText,
      title: "Warsztaty architektoniczne",
      duration: "w kilka dni",
      description:
        "Po 2-godzinnej sesji online otrzymujesz raport z doborem chipa, architekturą firmware, projektem profilu GATT i szczegółową wyceną.",
    },
    {
      Icon: PenTool,
      title: "Podpisanie umowy",
      duration: "ustalenie harmonogramu",
      description:
        "Ustalamy kamienie milowe, terminy dostaw i sposób komunikacji postępów przez cały projekt.",
    },
    {
      Icon: Search,
      title: "Bring-up na płytce Nordic",
      duration: "1-2 tygodnie",
      description:
        "Uruchamiamy podstawowy szkielet firmware na wybranej płytce (dev kit lub Nordic Thingy), potwierdzamy komunikację BLE i dokumentujemy peryferia.",
    },
    {
      Icon: Wrench,
      title: "Etapowe programowanie firmware",
      duration: "min. 2-3 miesiące",
      description:
        "Sterowniki peryferiów, stos BLE, logika aplikacji, OTA DFU i optymalizacja energetyczna - dostarczane etapami z testami na realnym sprzęcie.",
    },
    {
      Icon: Smartphone,
      title: "Integracja aplikacji mobilnej",
      duration: "równolegle lub po kolei",
      description:
        "Towarzysząca aplikacja iOS i Android powstaje z tym samym projektem profilu GATT, dzięki czemu obie strony działają ze sobą poprawnie.",
    },
    {
      Icon: Gauge,
      title: "Testy energetyczne i produkcyjne",
      duration: "min. 2 tygodnie",
      description:
        "Mierzymy realny pobór prądu na docelowym sprzęcie, wspieramy procedury testów produkcyjnych i walidację DFU.",
    },
    {
      Icon: CheckCircle,
      title: "Przekazanie i wsparcie",
      duration: "na stałe",
      description:
        "Otrzymujesz pełny kod źródłowy i dokumentację, albo kontynuujemy współpracę jako Twój długoterminowy partner od utrzymania firmware na Nordic.",
    },
  ],
  cta: {
    href: "#kontakt",
    text: "Zamów projekt na sprzęcie Nordic",
  },
};

export const aboutUsCopy: AboutUsCopy = {
  header: {
    h2: "O nas",
    h3: "Zespół wyspecjalizowany w Nordic Semiconductor",
    p1: "Jesteśmy małym, doświadczonym zespołem z Polski, który na co dzień pracuje z platformą Nordic - od nRF52 po najnowsze nRF54.",
    p2: "Michał i Tomasz prowadzą rozwój firmware na nRF Connect SDK i Zephyr RTOS, Kuba odpowiada za backend i infrastrukturę, a Mateusz projektuje aplikację towarzyszącą urządzeniu.",
    p3: "Zgodność z RODO",
    p4: "Dane w UE",
    Icon: ShieldCheck,
    imageAlt: "Zespół programujący firmware na sprzęcie Nordic nRF",
  },
  values: [
    {
      Icon: Users,
      title: "Pracujesz bezpośrednio z inżynierami",
      description:
        "Bez pośredników - osoba na rozmowie to osoba, która programuje Twój firmware na Nordic.",
    },
    {
      Icon: MapPin,
      title: "Europejski zespół, regulacje UE",
      description:
        "Działamy z Polski, rozumiemy wymagania CE, zgodność z RODO i wymagania rynku UE dla produktów opartych o sprzęt Nordic.",
    },
    {
      Icon: ShieldCheck,
      title: "Wąska specjalizacja, mniej ryzyka",
      description:
        "Pracujemy głównie z platformą Nordic Semiconductor - ta specjalizacja oznacza mniej niespodzianek przy propotypowaniu i szybszy czas dostarczenia projektu.",
    },
  ],
  cta: {
    text: "Napisz do nas",
    href: "#kontakt",
  },
};

export const faqCopy: FAQCopy = {
  header: {
    h2: "FAQ",
    h3: "Najczęściej zadawane pytania o programowanie na Nordic nRF",
  },
  faqs: faqsPl,
};

export const footerCopy: FooterCopy = {
  header: {
    h2: "Planujesz projekt na Nordic\xa0nRF?",
    p: "Umów się na darmową konsultację, opisz swój chip i wymagania, a otrzymasz uczciwą pierwszą ocenę od inżyniera specjalizującego się w Nordic Semiconductor.",
  },
  cta: {
    href: "#kontakt",
    text: "Umów darmową konsultację",
    p: "Świadczymy usługi programowania firmware na Nordic nRF52, nRF53 i nRF54 oraz prototypowania na Nordic Thingy - wraz z towarzyszącą aplikacją mobilną. Działamy z Polski, obsługujemy klientów w całej Europie.",
    email: "hello@bluesbrackets.com",
    phone: "+48 535 462 678",
  },
  links: {
    h4: "Na skróty",
    links: [
      { href: "/projects", text: "Projekty" },
      { href: "/blog", text: "Blog" },
      { href: "#uslugi", text: "Nasze usługi" },
      { href: "#proces", text: "Proces współpracy" },
      { href: "#faq", text: "FAQ" },
      { href: "/aplikacje-bluetooth", text: "Aplikacje mobilne Bluetooth" },
      {
        href: "/programowanie-firmware-ble",
        text: "Programowanie firmware BLE",
      },
      { href: "/prototypy-urzadzen-ble", text: "Prototypy urządzeń BLE" },
    ],
  },
  knowledgeBase: {
    h4: "Baza wiedzy",
    links: [
      {
        href: "/pl/blogs/is_bluetooth_le_right_for_your_product",
        text: "Czy Bluetooth LE to właściwa technologia do realizacji Twojego produktu lub MVP?",
      },
      {
        href: "/pl/blogs/bluetooth_le_product_architecture",
        text: "Architektura produktu opartego o Bluetooth LE",
      },
      {
        href: "/pl/blogs/ble_mobile_application_support",
        text: "Wsparcie BLE po stronie aplikacji mobilnej",
      },
      {
        href: "/pl/blogs/ble_embedded_development_platforms",
        text: "Najlepsze platformy rozwojowe embedded z Bluetooth LE",
      },
      {
        href: "/pl/blogs/auracast_mcu",
        text: "ESP32 Auracast: czy działa? Najlepsze MCU do LE Audio",
      },
      {
        href: "/pl/blogs/ble_hardware_2026",
        text: "Najlepszy hardware do rozwoju Bluetooth Low Energy BLE w 2026 roku",
      },
    ],
  },
  copyright: "bluesBrackets. Wszelkie prawa zastrzeżone.",
  privacyPolicy: "Polityka prywatności",
  privacyPolicyHref: "/policy",
  contactForm: {
    heading: "Napisz do nas",
    fields: {
      name: {
        label: "Imię i nazwisko",
        placeholder: "Jan Kowalski",
        requiredError: "Podaj imię i nazwisko",
      },
      email: {
        label: "Adres e-mail",
        placeholder: "jan@firma.pl",
        requiredError: "Podaj adres e-mail",
        invalidError: "Podaj poprawny adres e-mail",
      },
      topic: {
        label: "Opis projektu",
        placeholder:
          "Krótko opisz, na jakim chipie Nordic pracujesz i co ma robić firmware...",
        requiredError: "Opisz swój projekt",
        maxLengthError: "Opis może mieć maksymalnie 2000 znaków",
      },
    },
    submit: "Wyślij zapytanie",
    submitting: "Wysyłanie...",
    successMessage: "Wiadomość wysłana!",
    errorMessage: "Nie udało się wysłać wiadomości :(",
    responseTime: "Odpowiemy w ciągu 24 godzin w dni robocze.",
    emailSubjectPrefix: "Imię i nazwisko",
  },
};
